// The per-recipient in-app delivery row (In-App Notifications tech design §5). Mirrors the
// Escalation model's plain-options + class shape. The NotificationEvent content is nested one
// level deep, exactly as the API serializes it (notifications/_notification.json.jbuilder).
//
// `createdAt` is kept as the raw microsecond-precision ISO string the API sends: the keyset
// pager echoes `<createdAt>|<id>` back verbatim as the next `cursor.lt`, so truncating it to a
// Date (millisecond precision) would corrupt the anchor.

export interface NotificationEventOptions {
  category: string;
  kind: string;
  // The event's presentation tone — the email banner vocabulary (warning/error/success; null or
  // info = the kind's normal presentation), stamped for kinds whose copy has renderings
  // (OLIO-3231). Keys the chip's TONE_OVERRIDES so the row tones the way the same moment's
  // email banner does.
  tone: string | null;
  message: string;
  targetName: string | null;
  targetUrl: string | null;
}

export interface NotificationOptions {
  id: string;
  readAt: string | null;
  createdAt: string;
  notificationEvent: Partial<NotificationEventOptions>;
}

function getEventDefaults(): NotificationEventOptions {
  return {
    category: '',
    kind: '',
    tone: null,
    message: '',
    targetName: null,
    targetUrl: null,
  };
}

export class NotificationEvent {
  category: string;
  kind: string;
  tone: string | null;
  message: string;
  targetName: string | null;
  targetUrl: string | null;

  constructor(options: Partial<NotificationEventOptions> = {}) {
    const opts = { ...getEventDefaults(), ...options };

    this.category = opts.category;
    this.kind = opts.kind;
    this.tone = opts.tone;
    this.message = opts.message;
    this.targetName = opts.targetName;
    this.targetUrl = opts.targetUrl;
  }

  // The row's clickable label: the patient/target name, falling back to the generic "View"
  // when the API stored no target_name (§5 item 3 — "View" is never persisted server-side).
  get linkLabel(): string {
    return this.targetName?.trim() || 'View';
  }
}

function getDefaults(): NotificationOptions {
  return {
    id: '',
    readAt: null,
    createdAt: '',
    notificationEvent: {},
  };
}

export default class Notification {
  id: string;
  readAt: string | null;
  createdAt: string;
  notificationEvent: NotificationEvent;

  constructor(options: Partial<NotificationOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.readAt = opts.readAt;
    this.createdAt = opts.createdAt;
    this.notificationEvent = new NotificationEvent(opts.notificationEvent);
  }

  get isUnread(): boolean {
    return !this.readAt;
  }

  // The composite keyset anchor for "load older": `<createdAt>|<id>`, echoed verbatim into the
  // next request's `cursor.lt` (the API's Notification::QueryAttributes::Cursor parses it back).
  get cursor(): string {
    return `${this.createdAt}|${this.id}`;
  }
}
